<template>
  <div id="app">
    <router-view />

    <div v-if="showHomeOrderCallout" class="home-order-callout">
      {{ t('home.orderCallout') }}
    </div>

    <van-tabbar
      v-if="showTabbar"
      v-model="activeTab"
      route
      placeholder
      safe-area-inset-bottom
    >
      <van-tabbar-item to="/" icon="home-o">{{ t('tabbar.home') }}</van-tabbar-item>
      <van-tabbar-item to="/camera/multi" icon="photograph" class="tabbar-item-camera">{{ t('tabbar.cameraOrder') }}</van-tabbar-item>
      <van-tabbar-item to="/favorites" icon="like-o">{{ t('tabbar.favorites') }}</van-tabbar-item>
      <van-tabbar-item to="/profile" icon="user-o">{{ t('tabbar.profile') }}</van-tabbar-item>
    </van-tabbar>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'

import { useUserStore } from '@/stores/user'

const { t } = useI18n()
const route = useRoute()
const showTabbar = computed(() => !route.meta?.hideTabbar)
const showHomeOrderCallout = computed(() => showTabbar.value && route.path === '/')
const activeTab = computed({
  get: () => {
    const path = route.path || '/'

    if (path === '/' || path.startsWith('/food/') || path.startsWith('/article/')) return 0
    if (
      path.startsWith('/camera') ||
      path.startsWith('/upload') ||
      path.startsWith('/menu') ||
      path.startsWith('/dish-detail') ||
      path.startsWith('/order-conversation') ||
      path.startsWith('/ai-recommend')
    )
      return 1
    if (path.startsWith('/favorites')) return 2
    if (path.startsWith('/profile')) return 3
    return 0
  },
  set: () => {}
})

onMounted(() => {
  const userStore = useUserStore()
  userStore.initAuth()
})
</script>

<style lang="scss">
// 引入Vant样式
@import 'vant/lib/index.css';

// CSS变量定义
:root {
  // 主色调 - 新首页暖黄/橙色系
  --primary-color: #FFC400;
  --primary-light: #FFE08A;
  --primary-dark: #FF9F00;
  --app-accent-orange: #FF6300;
  --app-page-warm: #FFE6B1;
  --app-surface-cream: #FFF8E8;
  --app-card-ivory: #FFFDF2;
  --app-text-strong: #050505;
  --app-text-main: #2B2116;
  --app-text-muted: #7B6A58;
  --app-favorite: #FF4D4F;
  --app-font-body: 'Noto Sans CJK SC', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Spline Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  --app-font-display: 'Noto Serif CJK SC', 'Noto Sans CJK SC', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', serif;
  
  // 辅助色
  --success-color: #4CAF50;
  --warning-color: #FF9800;
  --danger-color: #F44336;
  --info-color: #2196F3;
  
  // 文字颜色
  --text-primary: #212121;
  --text-secondary: #757575;
  --text-hint: #BDBDBD;
  
  // 背景色
  --bg-primary: #FFFFFF;
  --bg-secondary: #F5F5F5;
  --bg-gray: #FAFAFA;
  
  // 边框色
  --border-color: #E0E0E0;
  --border-light: #F5F5F5;
  
  // 价格颜色
  --price-color: #F44336;

  // Tabbar
  --app-tabbar-height: 50px;
  --app-tabbar-offset: calc(var(--app-tabbar-height) + env(safe-area-inset-bottom, 0px));
}

// Vant主题覆盖
.van-button--primary {
  background-color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
  
  &:active {
    background-color: var(--primary-dark) !important;
    border-color: var(--primary-dark) !important;
  }
}

.van-button--danger {
  background-color: var(--danger-color) !important;
  border-color: var(--danger-color) !important;
}

.van-tabbar-item--active {
  color: var(--primary-color) !important;
}

.van-tabbar {
  background: #fff !important;
}

.tabbar-item-camera .van-tabbar-item__icon {
  position: relative;
  color: var(--app-text-main);
}

.tabbar-item-camera .van-tabbar-item__icon::after {
  content: '';
  position: absolute;
  top: -2px;
  right: -4px;
  width: 8px;
  height: 8px;
  background: var(--danger-color);
  border-radius: 50%;
  box-shadow: 0 0 0 2px #fff;
}

.van-nav-bar {
  background-color: var(--bg-primary) !important;
}

.van-cell {
  background-color: var(--bg-primary) !important;
}

.van-field__control {
  color: var(--text-primary) !important;
}

.van-stepper__plus {
  background-color: var(--info-color) !important;
  color: white !important;
}

.van-stepper__minus:not(.van-stepper__minus--disabled) {
  background-color: var(--bg-secondary) !important;
  color: var(--text-secondary) !important;
  border-color: var(--border-color) !important;
}

// 全局样式重置
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  width: 100%;
  height: 100%;
  font-family: var(--app-font-body);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-text-size-adjust: 100%;
  -ms-text-size-adjust: 100%;
  text-size-adjust: 100%;
  -webkit-tap-highlight-color: transparent;
}

#app {
  width: 100%;
  min-height: 100vh;
  min-height: -webkit-fill-available;
  background-color: var(--app-surface-cream);
  position: relative;
  overflow-x: hidden;
}

// 页面容器样式
.page-container {
  min-height: 100vh;
  min-height: -webkit-fill-available;
  width: 100%;
  box-sizing: border-box;
  padding-bottom: calc(50px + env(safe-area-inset-bottom));
}

// 响应式文字大小
@media screen and (max-width: 320px) {
  html {
    font-size: 14px;
  }
}

@media screen and (min-width: 375px) {
  html {
    font-size: 16px;
  }
}

@media screen and (min-width: 414px) {
  html {
    font-size: 17px;
  }
}

// 横屏适配
@media screen and (orientation: landscape) {
  .page-container {
    min-height: 100vh;
  }
}

// iOS安全区域适配
@supports (padding: max(0px)) {
  .page-container {
    padding-left: max(12px, env(safe-area-inset-left));
    padding-right: max(12px, env(safe-area-inset-right));
  }
}

// 移动端1px边框解决方案
.hairline-border {
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 200%;
    height: 200%;
    border: 1px solid #e5e5e5;
    transform: scale(0.5);
    transform-origin: 0 0;
    box-sizing: border-box;
    pointer-events: none;
  }
}

// 文字省略
.ellipsis {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.ellipsis-2 {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

// 安全区域适配
.safe-area-bottom {
  padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: env(safe-area-inset-bottom);
}

.home-order-callout {
  position: fixed;
  left: 37.5%;
  bottom: calc(var(--app-tabbar-offset) - 3px);
  z-index: 2001;
  transform: translateX(-50%);
  height: 26px;
  max-width: min(270px, calc(100vw - 32px));
  padding: 0 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #FFA61A;
  border-radius: 999px;
  background: #FFF9E7;
  color: var(--app-accent-orange);
  font-size: 14px;
  line-height: 1;
  white-space: nowrap;
  box-shadow: 0 6px 16px rgba(255, 154, 0, 0.1);
  pointer-events: none;
  overflow: visible;
}

.home-order-callout::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: -6px;
  width: 10px;
  height: 10px;
  border-right: 1px solid #FFA61A;
  border-bottom: 1px solid #FFA61A;
  background: #FFF9E7;
  transform: translateX(-50%) rotate(45deg);
}

// 隐藏滚动条但保持滚动
.hide-scrollbar {
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
}

// Google Material Symbols
// Google Material Symbols
// The 'material-symbols' package handles the font-face and basic class definition.
// We can override specific properties if needed, but normally .material-symbols-outlined 
// is provided by the imported CSS.
// However, ensuring our preferred settings:
.material-symbols-outlined {
  font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
  font-size: 24px;  /* Preferred icon size */
}

// Tabbar Camera Animation
.tabbar-item-camera {
  .van-tabbar-item__icon {
    animation: none;
  }
}
</style>
